import React, { useEffect, useMemo, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import { decode } from 'utils/crypto/crypto';
import { MicroBundleServiceInfo } from 'services/microbundleServiceInfo';
import { finalize } from 'rxjs/operators';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { dispatch } from 'redux/store';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import { UsersService } from 'services/users/users';
import { AccountsService } from 'services/accounts/accounts';
import { AdditionalBenefits } from 'views/Microbundle/AddNewMb/AdditionalBenefits/AdditionalBenefits';
import { NavButtons } from 'views/Microbundle/AddNewMb/commons/NavButtons';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/MicroBundle/aamaliServicesStyle';
import { HeadingLBold } from 'components/Typography/Typography';
import { TextWithIcon } from 'components/TextWithIcon/TextWithIcon';
import Button from 'components/CustomButtons/Button';
import { useSelector } from 'react-redux';
import { userData, accountNameSelector, upIdSelector, userIdSelector } from 'redux/modules/session/selectors';
import { SubmitSalesLeads } from 'services/submitSalesLeads';
import StatusDialog from 'components/StatusDialog/StatusDialog';
import { useHistory } from 'react-router-dom';
import { COMMITMENT_STATUS } from 'views/Numbers/constants';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import { findElementWithHighestDate, isServiveActiveOrPending } from 'utils/helpers';
import { ARBOR_E_FORM_ID, FAILURE, MK_365_PART_NUMBERS, ROUTES, SUCCESS } from 'views/Microbundle/AddNewMb/constants';
import { getLanguage } from 'utils/language/language';
import { getEmailMap, getTemplateKeys } from 'redux/modules/dynamicLabels/selectors';
import { TagManager } from 'commons/gtm/tagManager';
import { status } from '../common/BandwidthOnDemand/constants';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isms365Enabled } from 'utils/killswitch';

interface IAamaliServicesProps {
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
			billingType: string;
			accountType: string;
			upAccountId: string;
			accountId: string;
			accountNumber: string;
		};
	};
	accountName: string;
	tariffPlan: SingleCustomerProduct | undefined;
	commitmentDetails: CommitmentDetails[];
}
// @ts-ignore
const useStyles = makeStyles(styles);

const AamaliServices = ({ match, accountName, tariffPlan, commitmentDetails }: IAamaliServicesProps) => {
	const classes = useStyles();
	const history = useHistory();
	const { phoneNumberEncoded, billingType, accountType, upAccountId, accountId, accountNumber } = match.params;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const [productInfo, setProductInfo] = useState<any>([]);
	const [checkedState, setCheckedState] = useState<boolean[]>(new Array().fill(false));
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
	const [selectedServices, setSelectedServices] = useState([]);
	const [documentId, setDocumentId] = useState<number>();
	const [loading, setLoading] = useState<boolean>(false);
	const [success, setSuccess] = useState(false);
	const [refNumber, setRefNumber] = useState<string>('');
	const userDetails = useSelector(userData);
	const [disableActivate, setDisableActivate] = useState<boolean>(false);
	const organisationName = useSelector(accountNameSelector);
	const templateKeys = useSelector(getTemplateKeys);
	const emailMap = useSelector(getEmailMap);
	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);
	const remoteConfig = useSelector(remoteConfigSelector);
	const enableMs365 = isms365Enabled(remoteConfig);

	//services will be fetched when the language changes
	const language = getLanguage();
	//If active commitment is not found then return undefined.
	const activeCommitment: SingleCommitmentDetails | undefined = useMemo(() => {
		const commitments: SingleCommitmentDetails[] = commitmentDetails?.[0]?.commitmentDetails;
		if (commitments?.length > 0) {
			/** find all active commitments and return object with highest endDate, if no result then do it for inactive commitments */
			const activeCommitments = commitments.filter((obj) => obj.status === COMMITMENT_STATUS.ACTIVE);
			const maxEndDateActive = findElementWithHighestDate(activeCommitments);

			if (maxEndDateActive) return maxEndDateActive;

			const inactiveCommitments = commitments.filter((obj) => obj.status === COMMITMENT_STATUS.INACTIVE);
			return findElementWithHighestDate(inactiveCommitments);
		}
	}, [commitmentDetails]);

	const fetchServiceDetails = () => {
		UsersService.getCorpUserDepartmentDataForNumber(upAccountId).subscribe((data) => {
			setLoading(true);
			if (data && activeCommitment?.productId) {
				AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
					const _documentId = accounts.find(({ accountId }) => accountId === data.accountId)?.documentId;
					setDocumentId(_documentId);
					const payload = {
						crNumber: _documentId,
						subscriberNumber: phoneNumber,
						productId: activeCommitment?.productId,
						billingType: billingType,
						accountType: accountType,
					};
					MicroBundleServiceInfo.getMicroBundleServiceInfo({ ...payload })
						.pipe(
							finalize(() => {
								dispatch(hideLoadingIndicator());
								setLoading(false);
							})
						)
						.subscribe(({ response }) => {
							const resp = response.data;
							if (resp) {
								const restructuredData =
									resp?.aamaliResponseList.length > 0 &&
									resp.aamaliResponseList.map((prod: any) => {
										return {
											name: prod?.serviceName,
											partNumber: prod?.serviceProductId,
											serviceDetails: prod?.serviceDetails,
											status: prod?.status,
										};
									});
								setProductInfo(restructuredData);
							}
						});
				});
			} else {
				ECommerceService.getGroupContent(idConfig.MICROBUNDLE.PLANS[env]).subscribe(({ response }: any) => {
					ECommerceService.getContentByPartNumber(response.catalogEntryView[0]?.partNumber).subscribe(
						({ response }: any) => {
							setLoading(false);
							const arbor_form_ids = response?.catalogEntryView[0]?.attributes?.filter(
								(el: any) => el.identifier == ARBOR_E_FORM_ID
							);
							const formIds = arbor_form_ids.map((id: any) => id?.values[0].value);
							ECommerceService.getGroupContent(idConfig.MICROBUNDLE.PRODUCT_INFO[env]).subscribe(
								({ response }: any) => {
									setLoading(false);
									const resp = response?.catalogEntryView;
									const filteredResponse = formIds.map(
										(id: string) =>
											resp?.filter((service: types.ecommerce.CatalogEntryView) => {
												return service.partNumber === id;
											})?.[0]
									);
									if (setProductInfo) setProductInfo(filteredResponse);
								}
							);
						}
					);
				});
			}
		});
	};
	useEffect(() => {
		dispatch(showLoadingIndicator());
		fetchServiceDetails();
	}, [upAccountId, language, commitmentDetails]);

	useEffect(() => {
		const serviceData: any = [];
		if (productInfo.length > 0) {
			productInfo.forEach((product: any, index: any) => {
				if (
					checkedState[index] &&
					!isServiveActiveOrPending(product.status) &&
					(!MK_365_PART_NUMBERS.includes(product.partNumber) ||
						(MK_365_PART_NUMBERS.includes(product.partNumber) && enableMs365))
				) {
					serviceData.push({
						serviceProductId: product.partNumber,
						serviceProductName: product.name,
						emailAddresses: emailMap[product.partNumber],
					});
				}
			});
		}
		setSelectedServices(serviceData);
	}, [checkedState]);

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const closeStatusModal = () => {
		setIsStatusModalOpen(false);
		fetchServiceDetails();
	};

	const openStatusModal = () => {
		setIsStatusModalOpen(true);
	};

	const getSelectedServicesName = () => {
		return selectedServices.map((service: any) => service.serviceProductName);
	};

	const isNextDisabled = () => {
		if (selectedServices.length > 0) return false;
		return true;
	};

	const submitRequest = () => {
		const payload = {
			productId: activeCommitment?.productId,
			productName: `${translate('mb.plan', tariffPlan?.name || '')}`,
			phone: userDetails.contactNumber,
			serviceList: [...selectedServices],
			organizationName: organisationName,
			crNumber: documentId,
			name: userDetails.userName,
			email: userDetails.email,
			subscriberNo: phoneNumber,
			accountNumber: accountNumber,
			templateKey: templateKeys.aamaliPlusExisting, // email template key
		};
		setDisableActivate(true);
		SubmitSalesLeads.submitSalesLeads({
			...payload,
		}).subscribe(
			(response) => {
				setDisableActivate(false);
				setSuccess(true);
				setRefNumber(response?.response?.data?.referenceNo);
				setG4Tag('My Numbers:Aamali+ Services:plan Activate Request submit success', {
					event: 'My Services Request Submit',
					referenceNum: response?.response?.data?.referenceNo,
				});
				openStatusModal();
				closeModal();
			},
			(error) => {
				setDisableActivate(false);
				setSuccess(false);
				setG4Tag('My Numbers:Aamali+ Services:plan Activate Request not processed', {
					event: 'My Services Request Submit',
				});
				openStatusModal();
				closeModal();
			}
		);
	};

	const setG4Tag = (step: string, rest?: any) => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: step,
				accountID: accId,
				billingID: accountNumber,
				language: getLanguage(),
				accountNumber: phoneNumber,
				userId: userId,
				...rest,
			},
		});
	};

	useEffect(() => {
		setG4Tag(`My Numbers: A'amali+ Services`);
	}, []);
	return (
		<>
			<BreadcrumbsItem to={match.path}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.aamali.services'
			)}`}</BreadcrumbsItem>
			<>
				<AdditionalBenefits
					checkedState={checkedState}
					setCheckedState={setCheckedState}
					productInfo={productInfo}
					newCustomer={!activeCommitment}
					planName={tariffPlan?.name}
					isServiceLoading={loading}
					activeCommitment={activeCommitment}
				/>
				<StatusDialog
					isModalOpen={isStatusModalOpen}
					closeModal={closeStatusModal}
					heading={success ? translate('mb.thankyou.header') : translate('mb.failure.header')}
					desc={success ? translate('mb.thankyou.body', refNumber) : translate('mb.failure.body')}
					status={success ? SUCCESS : FAILURE}
					referenceNumber={refNumber}
					desc2={success ? translate('mb.thankyou.body2') : translate('mb.failure.body2')}
					footer={success ? translate('mb.thankyou.body3') : ''}
				/>
				<CustomDialog
					isOpen={isModalOpen}
					width={500}
					hideDialog={() => closeModal()}
					dialogBody={
						<div className={classes.modalWrapper}>
							<HeadingLBold>{translate('activation.request')}</HeadingLBold>
							<TextWithIcon items={getSelectedServicesName()} />
							<br />
							<Button
								className={classes.modalButton}
								color="primary-light"
								onClick={submitRequest}
								disabled={disableActivate}
							>
								{translate('submit.activation.request')}
							</Button>{' '}
						</div>
					}
				/>
				<div className={classes.navButtons}>
					<NavButtons
						handleNext={() => {
							const selectedProductDetails = selectedServices.map((service: any) => {
								return {
									item_name: service.serviceProductName,
									item_id: service.serviceProductId,
								};
							});
							setIsModalOpen(true);
							setG4Tag('My Numbers:Aamali+ Services:plan Activate button click', {
								items: selectedProductDetails,
							});
						}}
						handleBack={() => {
							history.push(ROUTES.NUMBERS);
						}}
						nextType={'submit'}
						nextText={translate('button.activate')}
						className={classes.navButtonWrapper}
						disabled={isNextDisabled()}
					/>
				</div>
			</>
		</>
	);
};

export default AamaliServices;
